// AIDEV-NOTE: PLAT-17818 — the sysadmin Live Preview renders the raw `body` and is overwritten
// with the editor's HTML on every change, so it never runs the server-side credit injection in
// ReferenceMaterial#body_html. This mirrors that markup client-side. The tag names and class
// list MUST stay in lockstep with Shared::ReferenceMaterialHelper#reference_material_image_credits
// and ReferenceMaterial#append_image_credits, or Preview stops matching the real view again.

const CREDITS_ATTRIBUTE = 'data-image-credits';
const WRAPPER_CLASS = 'image-with-credit';
const CREDIT_CLASS = 'image-credit small_text';
const BREAK_TEXT_CLASS = 'fr-dib';
const ALIGNMENT_CLASSES = ['fr-dii', 'fr-fil', 'fr-fir'];

export type ImageCreditsMap = Record<string, string>;

const imageModelId = (snippet: string): string => (snippet.match(/\d+/) || [''])[0];

export const readImageCredits = (container: HTMLElement): ImageCreditsMap => {
  try {
    return JSON.parse(container.getAttribute(CREDITS_ATTRIBUTE) || '{}') as ImageCreditsMap;
  } catch {
    return {};
  }
};

export const writeImageCredits = (container: HTMLElement, credits: ImageCreditsMap): void => {
  container.setAttribute(CREDITS_ATTRIBUTE, JSON.stringify(credits));
};

const creditNode = (tag: 'span' | 'div', credits: string, extraClasses: string[] = []) => {
  const credit = document.createElement(tag);
  credit.className = [CREDIT_CLASS, ...extraClasses].join(' ').trim();
  credit.textContent = `Image Credit: ${credits}`;

  return credit;
};

// Break-text images already own their line, so their credit stays a plain block sibling —
// same split as ReferenceMaterial#append_image_credits.
const appendBlockCredit = (image: HTMLImageElement, credits: string) => {
  image.after(creditNode('div', credits, [...image.classList]));
};

const wrapInlineImage = (image: HTMLImageElement, credits: string) => {
  const wrapper = document.createElement('span');
  const alignment = ALIGNMENT_CLASSES.filter(name => image.classList.contains(name));
  wrapper.className = [WRAPPER_CLASS, ...alignment].join(' ');

  image.replaceWith(wrapper);
  wrapper.appendChild(image);
  wrapper.appendChild(creditNode('span', credits));
};

// Safe to call repeatedly on an already-injected DOM. `livePreviewEvent` resets `innerHTML` first,
// so the guards below only matter for the mount-time call in TextEditor's useEffect.
const injectImageCredits = (container: HTMLElement | null): void => {
  if (!container) return;

  const credits = readImageCredits(container);
  if (Object.keys(credits).length === 0) return;

  container.querySelectorAll<HTMLImageElement>('img[data-snippet]').forEach((image) => {
    if (image.parentElement?.classList.contains(WRAPPER_CLASS)) return;
    if (image.nextElementSibling?.classList.contains('image-credit')) return;

    const creditsText = credits[imageModelId(image.dataset.snippet || '')];
    if (!creditsText) return;

    if (image.classList.contains(BREAK_TEXT_CLASS)) appendBlockCredit(image, creditsText);
    else wrapInlineImage(image, creditsText);
  });
};

export default injectImageCredits;
